// WebSocket transport for agents on the Grok, GPT-Live and studio runtimes
// (service-grok, service-live, service-studio; same /web-call contract). Mic
// PCM16 @ 24kHz goes up as binary frames, agent audio comes back the same way,
// lifecycle and runtime events arrive as JSON text frames. Everything durable
// (recording, transcripts, call docs) happens server-side. Ported from the
// embed's WebCallManager; playback now runs through the shared audio context
// so it is unlocked by the user's tap and metered.
import { anonymousIdentity } from './auth.js';
import { toTranscript } from './transcript.js';
import { RuntimeMismatchError, type Transport, type TransportHooks } from './types.js';

const GROK_VOICE_SERVICE_URL = 'https://service-grok-291799762709.us-west1.run.app';
const LIVE_VOICE_SERVICE_URL = 'https://service-live-291799762709.us-west1.run.app';
const STUDIO_VOICE_SERVICE_URL = 'https://service-studio-291799762709.us-west1.run.app';

/** WebSocket runtimes by the `runtime` service-ws names in its 409; unknown names keep grok. */
export const webSocketServiceUrl = (runtime: string | undefined): string =>
  runtime === 'live' ? LIVE_VOICE_SERVICE_URL : runtime === 'studio' ? STUDIO_VOICE_SERVICE_URL : GROK_VOICE_SERVICE_URL;

const SAMPLE_RATE = 24000;
const FRAME_MS = 100;

const CAPTURE_WORKLET = `
class PcmCapture extends AudioWorkletProcessor {
  constructor() { super(); this.buf = []; this.len = 0; this.frame = Math.round(sampleRate * ${FRAME_MS} / 1000); }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) {
      this.buf.push(new Float32Array(ch)); this.len += ch.length;
      while (this.len >= this.frame) {
        const out = new Int16Array(this.frame);
        let filled = 0;
        while (filled < this.frame) {
          const head = this.buf[0];
          const take = Math.min(head.length, this.frame - filled);
          for (let i = 0; i < take; i++) {
            const s = Math.max(-1, Math.min(1, head[i]));
            out[filled + i] = s < 0 ? s * 0x8000 : s * 0x7fff;
          }
          if (take === head.length) this.buf.shift(); else this.buf[0] = head.subarray(take);
          filled += take; this.len -= take;
        }
        this.port.postMessage(out, [out.buffer]);
      }
    }
    return true;
  }
}
registerProcessor('pcm-capture', PcmCapture);
`;

export const webSocketTransport = (hooks: TransportHooks, serviceUrl: string = GROK_VOICE_SERVICE_URL): Transport => {
  let ws: WebSocket | null = null;
  let captureCtx: AudioContext | null = null;
  let localStream: MediaStream | null = null;
  let worklet: AudioWorkletNode | null = null;
  const playing = new Set<AudioBufferSourceNode>();
  let nextPlayTime = 0;
  let muted = false;
  let status: 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error' = 'idle';
  let gen = 0;

  const setStatus = (s: typeof status) => {
    status = s;
    if (s !== 'idle') hooks.status(s);
  };

  const playPcm = (pcm: Int16Array) => {
    const ctx = hooks.ctx;
    if (pcm.length === 0 || ctx.state === 'closed') return;
    if (ctx.state === 'suspended') void ctx.resume();
    const float = new Float32Array(pcm.length);
    for (let i = 0; i < pcm.length; i++) float[i] = pcm[i]! / 32768;
    // A 24kHz buffer plays correctly in a context running at any rate.
    const buffer = ctx.createBuffer(1, float.length, SAMPLE_RATE);
    buffer.getChannelData(0).set(float);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(hooks.agentOut);
    const startAt = Math.max(nextPlayTime, ctx.currentTime + 0.03);
    src.start(startAt);
    nextPlayTime = startAt + buffer.duration;
    playing.add(src);
    src.onended = () => playing.delete(src);
  };

  const flushPlayback = () => {
    for (const src of playing) { try { src.stop(); } catch { /* already stopped */ } }
    playing.clear();
    nextPlayTime = 0;
  };

  const cleanup = () => {
    flushPlayback();
    if (worklet) {
      worklet.port.onmessage = null;
      worklet.disconnect();
      worklet = null;
    }
    localStream?.getTracks().forEach((t) => t.stop());
    localStream = null;
    captureCtx?.close().catch(() => {});
    captureCtx = null;
    if (ws && ws.readyState <= WebSocket.OPEN) { try { ws.close(); } catch { /* already closed */ } }
    ws = null;
  };

  const fail = (message: string) => {
    hooks.error(message);
    setStatus('error');
    cleanup();
  };

  const connect = async (agent: string, token: string | null) => {
    const my = ++gen;
    const aborted = () => my !== gen;
    setStatus('connecting');

    const bearer = token ?? (await anonymousIdentity()).idToken;
    if (aborted()) return;

    const assistantLine = agent.startsWith('+') ? agent : `+1${agent}`;
    const [session, stream] = await Promise.all([
      fetch(`${serviceUrl}/web-call/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${bearer}` },
        body: JSON.stringify({ assistantLine }),
      }).then(async (r) => {
        if (r.status === 409) throw new RuntimeMismatchError('webrtc');
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error((err as { error?: string }).error || `Session creation failed: ${r.status}`);
        }
        return r.json() as Promise<{ wsUrl: string }>;
      }),
      navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, sampleRate: SAMPLE_RATE } }),
    ]);

    if (aborted()) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    localStream = stream;
    hooks.callerStream(stream);
    nextPlayTime = 0;

    const socket = new WebSocket(session.wsUrl);
    socket.binaryType = 'arraybuffer';
    ws = socket;

    socket.onopen = async () => {
      if (aborted()) return;
      try {
        // Capture runs at 24kHz so the worklet emits the PCM the service expects.
        const ctx = new AudioContext({ sampleRate: SAMPLE_RATE });
        captureCtx = ctx;
        const url = URL.createObjectURL(new Blob([CAPTURE_WORKLET], { type: 'text/javascript' }));
        await ctx.audioWorklet.addModule(url);
        URL.revokeObjectURL(url);
        const node = new AudioWorkletNode(ctx, 'pcm-capture');
        worklet = node;
        node.port.onmessage = (e: MessageEvent<Int16Array>) => {
          if (socket.readyState === WebSocket.OPEN && !muted) socket.send(e.data.buffer);
        };
        ctx.createMediaStreamSource(stream).connect(node);
        setStatus('connected');
      } catch (err) {
        fail(err instanceof Error ? err.message : 'Microphone capture failed');
      }
    };

    socket.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        playPcm(new Int16Array(event.data));
        return;
      }
      try {
        const msg = JSON.parse(event.data as string);
        const transcript = toTranscript(msg);
        if (transcript) hooks.transcript(transcript);
        else if (msg.type === 'runtime.event' && msg.event) hooks.event(msg.event);
        // Barge-in: the model already stopped; drop whatever is still queued.
        else if (msg.type === 'input_audio_buffer.speech_started') flushPlayback();
        else if (msg.type === 'error') console.error('[talk:grok] server error:', JSON.stringify(msg.error));
      } catch { /* non-JSON text frame */ }
    };

    socket.onclose = () => {
      if (status === 'connected' || status === 'connecting') setStatus('disconnected');
      cleanup();
    };
    socket.onerror = () => {
      if (status === 'connecting') fail('Connection failed');
    };
  };

  const call = async (agent: string, token: string | null) => {
    try {
      await connect(agent, token);
    } catch (err) {
      if (err instanceof RuntimeMismatchError) throw err;
      fail(err instanceof Error ? err.message : 'Failed to connect');
    }
  };

  const hangUp = () => {
    if (status === 'idle' || status === 'disconnected') return;
    gen++;
    setStatus('disconnected');
    // The service finalizes recording and transcripts on its own.
    try { ws?.send(JSON.stringify({ type: 'hangup' })); } catch { /* closing anyway */ }
    try { ws?.close(1000); } catch { /* already closed */ }
    cleanup();
  };

  const setMuted = (m: boolean) => {
    muted = m;
    localStream?.getAudioTracks().forEach((t) => { t.enabled = !m; });
  };

  return { call, hangUp, setMuted };
};
